import {
  Alert,
  Box,
  CircleIcon,
  CommonPopupCrud,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@common";

// Helper to format camelCase / snake_case / PascalCase keys into title case (e.g. testSuites -> Test Suites)
const formatKeyName = (key) => {
  if (!key) return "";
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/[-_]+/g, " ")
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
};

// Helper to get printable string from an item
const getItemText = (item) => {
  if (item === null || item === undefined) return "";
  if (typeof item === "string") return item;
  if (typeof item === "number" || typeof item === "boolean") return String(item);
  if (typeof item === "object") {
    return (
      item?.name ||
      item?.title ||
      item?.label ||
      item?.code ||
      item?.id ||
      item?.message ||
      JSON.stringify(item)
    );
  }
  return String(item);
};

// Helper to transform any list/data/object into an array of sections: [{ title, items }]
const parseSections = (data) => {
  if (!data) return [];

  // If data is already an array
  if (Array.isArray(data)) {
    return data.length > 0 ? [{ title: "Details", items: data }] : [];
  }

  // If data is an object (like { testSuites: [...], flowSuites: [] })
  if (typeof data === "object") {
    const sections = [];

    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          sections.push({
            title: formatKeyName(key),
            items: value,
          });
        }
      } else if (value && typeof value === "object") {
        const subEntries = Object.entries(value);
        if (subEntries.length > 0) {
          sections.push({
            title: formatKeyName(key),
            items: subEntries.map(([k, v]) => `${formatKeyName(k)}: ${getItemText(v)}`),
          });
        }
      } else if (value !== null && value !== undefined && value !== "") {
        sections.push({
          title: formatKeyName(key),
          items: [value],
        });
      }
    });

    return sections;
  }

  // If primitive string/number
  return [{ title: "Details", items: [data] }];
};

function CommonAlertPopup({
  title,
  open,
  onClose,
  onConfirm,
  confirmText = "OK",
  severity,
  message,
  list,
  data,
  alertData,
  loading,
  ...props
}) {
  if (open !== undefined && !open) {
    return null;
  }

  // Support alertData object (full response format) or individual props
  const rawResponse =
    alertData ||
    (props?.ok !== undefined || props?.code || props?.errorData ? props : null);

  const resolvedMessage =
    message ||
    rawResponse?.message ||
    (typeof list === "string" ? list : "") ||
    "";

  const resolvedSeverity =
    severity ||
    (rawResponse?.warn ? "warning" : rawResponse?.error ? "error" : "error");

  const resolvedTitle =
    title ||
    (rawResponse?.code ? formatKeyName(rawResponse.code) : "Alert");

  // Determine details source from data, alertData.data, alertData.errorData, or list
  let detailsSource =
    data ||
    rawResponse?.data ||
    rawResponse?.errorData ||
    list;

  // If list itself was passed the full API response object (e.g. list={response})
  if (
    detailsSource &&
    typeof detailsSource === "object" &&
    !Array.isArray(detailsSource)
  ) {
    if (detailsSource.data || detailsSource.errorData) {
      detailsSource = detailsSource.data || detailsSource.errorData;
    }
  }

  const sections = parseSections(detailsSource);

  return (
    <CommonPopupCrud
      mediumScreen={true}
      heading={resolvedTitle}
      close={onClose}
      buttonAlignRight={true}
      handleSubmit={() => {
        onConfirm?.();
        // Handle Close inside OnConfirm
      }}
      saveText={confirmText}
      alertDeleteColor={resolvedSeverity === "error"}
      hideSaveAndNext={true}
      loading={loading}
    >
      <Alert
        severity={resolvedSeverity}
        sx={{ width: "100%", alignItems: "flex-start" }}
      >
        {resolvedMessage && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: "0.9rem",
              lineHeight: 1.5,
              mb: sections.length > 0 ? 1 : 0,
            }}
          >
            {resolvedMessage}
          </Typography>
        )}

        {sections.length > 0 && (
          <Box sx={{ mt: 1, width: "100%" }}>
            {sections.map((section, sIndex) => (
              <Box
                key={sIndex}
                sx={{ mb: sIndex < sections.length - 1 ? 1.5 : 0 }}
              >
                {section.title && (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      color: "text.primary",
                      mt: sIndex > 0 ? 1 : 0.5,
                      mb: 0.5,
                    }}
                  >
                    {section.title}:
                  </Typography>
                )}

                <Box sx={{ pl: 0.5 }}>
                  {section.items.map((item, index) => (
                    <ListItem disablePadding key={index}>
                      <ListItemButton
                        sx={{
                          py: 0.25,
                          px: 0.5,
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <CircleIcon
                            sx={{
                              fontSize: "0.45rem",
                              color: "#876e6e",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={getItemText(item)}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              fontSize: "0.85rem",
                              wordBreak: "break-word",
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Alert>
    </CommonPopupCrud>
  );
}

export default CommonAlertPopup;